import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Auth from "../../services/context/store";
import { ACTION_TYPES } from "../../services/actions/actions";
import LogoutButton from "../LogoutButton";
import { Link, useNavigate } from "react-router-dom";
import {
  FaCreditCard,
  FaFile,
  FaHome,
  FaMoneyBill,
  FaRegCreditCard,
  FaServer,
  FaSignOutAlt,
  FaUser,
  FaUsers,
} from "react-icons/fa";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const Icons = [
  <FaHome />,
  <FaServer />,
  <FaCreditCard />,
  <FaMoneyBill />,
  <FaUsers />,
  <FaUser />,
  <FaRegCreditCard />,
  <FaFile />,
];

export default function Layout({ children }) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const navigate = useNavigate();

  const [, dispatch] = React.useContext(Auth);
  const token = localStorage.getItem("token");

  const handleLogOut = () => {
    dispatch({ type: ACTION_TYPES.LOGOUT, payload: { user: null } });
    localStorage.clear("token");
    navigate("/login");
  };

  const test = React.useCallback(async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}test`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        token: localStorage.getItem("token"),
      },
    });

    const data = await response.json();

    if (!data.user) {
      dispatch({ type: ACTION_TYPES.LOGOUT, payload: { user: null } });
      localStorage.clear("token");
      navigate("/login");
    } else {
      dispatch({ type: ACTION_TYPES.LOGIN, payload: { user: data.user } });
    }
  }, [dispatch, navigate]);

  React.useEffect(() => {
    test();
  }, [test, token]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" className="!bg-[#1B4242]" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="end"
            sx={{
              marginRight: 5,
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={open}
        onClick={handleDrawerClose}
        className="!bg-[#1B4242]"
      >
        <DrawerHeader className="!bg-[#1B4242]">
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon className="text-white" />
            ) : (
              <ChevronLeftIcon className="text-white" />
            )}
          </IconButton>
        </DrawerHeader>
        <List className="!bg-[#1B4242]">
          {[
            "Dashboard",
            "Activity",
            "Deposit",
            "Withdrawal",
            "Referral",
            "Profile",
            "Subscription",
            "Verify Account",
          ].map((text, index) => (
            <ListItem key={text} disablePadding sx={{ display: "block" }}>
              <Link
                to={`${
                  text === "Dashboard" ? "/" : "/" + text.toLocaleLowerCase()
                }`}
                className="flex no-underline text-white"
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 0,
                      justifyContent: "center",
                      color: "white",
                    }}
                  >
                    {Icons[index]}
                  </ListItemIcon>
                  <ListItemText
                    primary={text}
                    sx={{ opacity: open ? 1 : 0 }}
                    className="!font-bold"
                  />
                </ListItemButton>
              </Link>
            </ListItem>
          ))}
        </List>
        <List className="!bg-[#1B4242] h-screen">
          <ListItem disablePadding sx={{ display: "block" }}>
            {open ? (
              <LogoutButton onClick={handleLogOut} />
            ) : (
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                }}
                onClick={handleLogOut}
              >
                <FaSignOutAlt color="red" />{" "}
              </ListItemButton>
            )}
          </ListItem>
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3 }}
        className="bg-[#092635]"
        onClick={handleDrawerClose}
      >
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  );
}
